import { type RefObject, useEffect } from 'react';
import { appContext } from '../../app/context';
import { commitTextDraft, draftFromLayer, textLayerAt } from '../../app/textFlow';
import { DEFAULT_TEXT_LINE_HEIGHT } from '../../core/paint/text';
import { useEditorStore } from '../../state/editorStore';
import { clientToDoc } from '../pointer';

/**
 * The text tool: click to place text, click existing text to edit it.
 *
 * Clicking while a draft is open finishes that draft first. Silently starting a
 * second one would lose the first, and silently dropping the click would look
 * like a broken tool.
 */
export function useTextTool(stageRef: RefObject<HTMLDivElement | null>): void {
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const onPointerDown = (event: PointerEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      const store = useEditorStore.getState();
      if (store.activeTool !== 'text') return;

      if (store.textDraft) {
        const draft = store.textDraft;
        store.setTextDraft(null);
        commitTextDraft(appContext, draft);
        event.preventDefault();
        return;
      }

      const docPoint = clientToDoc(event.clientX, event.clientY, stage, store.viewport);
      const existing = textLayerAt(store.doc, docPoint.x, docPoint.y);
      if (existing) {
        store.setTextDraft(draftFromLayer(existing));
        store.setActiveLayer(existing.id);
        event.preventDefault();
        return;
      }

      store.setTextDraft({
        layerId: null,
        x: docPoint.x,
        y: docPoint.y,
        text: '',
        fontSize: store.text.fontSize,
        fontFamily: store.text.fontFamily,
        bold: store.text.bold,
        italic: store.text.italic,
        color: store.color,
        lineHeight: DEFAULT_TEXT_LINE_HEIGHT,
      });
      event.preventDefault();
    };

    stage.addEventListener('pointerdown', onPointerDown);
    return () => stage.removeEventListener('pointerdown', onPointerDown);
  }, [stageRef]);
}
